import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useAuth } from "@/App";
import TopNav from "@/components/TopNav";
import SubscriptionBadge from "@/components/SubscriptionBadge";
import PlanChangeCard from "@/components/PlanChangeCard";
import {
  Loader2, CreditCard, ArrowLeft, CheckCircle, XCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function CorporateBilling() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [changing, setChanging] = useState(false);

  useEffect(() => {
    if (!auth?.user) return;
    fetchData();
  }, [auth?.user]);

  const fetchData = async () => {
    try {
      const [subRes, plansRes] = await Promise.all([
        axios.get(`${API}/tenant/admin/subscription`, { headers: getHeaders() }),
        axios.get(`${API}/tenant/plans?type=CORPORATE`),
      ]);
      setSubscription(subRes.data.subscription);
      setCurrentPlan(subRes.data.plan);
      setAvailablePlans(plansRes.data.plans || []);
    } catch (err) {
      toast.error("Failed to load subscription data");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePlan = async (planId) => {
    if (!window.confirm("Change subscription plan? Your hub access will update accordingly.")) return;
    setChanging(true);
    try {
      await axios.put(
        `${API}/tenant/admin/subscription/plan`,
        { plan_id: planId },
        { headers: getHeaders() }
      );
      toast.success("Plan updated successfully");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change plan");
    } finally {
      setChanging(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm("Cancel subscription? Your hubs will be restricted after the current period.")) return;
    try {
      await axios.post(`${API}/tenant/admin/subscription/cancel`, {}, { headers: getHeaders() });
      toast.success("Subscription cancelled");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to cancel");
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "N/A";
    return new Date(iso).toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      <TopNav />
      <div className="container mx-auto px-4 py-6 max-w-4xl">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <h1 className="text-2xl font-bold mb-6">Billing & Subscription</h1>

        <Tabs defaultValue="overview">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="change">Change Plan</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="mt-4 space-y-4">
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4">Current Plan</h2>
              {subscription ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <SubscriptionBadge
                      status={subscription.status}
                      planName={currentPlan?.name}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Billing Period</span>
                      <p className="font-medium capitalize">{subscription.billing_period}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Status</span>
                      <p className="font-medium capitalize">{subscription.status}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Period Start</span>
                      <p className="font-medium">{formatDate(subscription.current_period_start)}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Period End</span>
                      <p className="font-medium">{formatDate(subscription.current_period_end)}</p>
                    </div>
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Selected {Object.values(subscription.selected_hubs || {}).filter(Boolean).length} of{" "}
                    {currentPlan ? Object.values(currentPlan.hubs || {}).filter(Boolean).length : 0} hubs
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleCancel}
                      disabled={changing || subscription.status === "CANCELLED"}
                    >
                      Cancel Subscription
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-muted-foreground">No active subscription</p>
              )}
            </Card>

            {subscription?.stripe_subscription_id && (
              <Card className="p-6">
                <h2 className="text-lg font-semibold mb-4">Payment Method</h2>
                <p className="text-sm text-muted-foreground">
                  Stripe subscription active. Manage billing in Stripe Customer Portal.
                </p>
              </Card>
            )}
          </TabsContent>

          {/* Change Plan Tab */}
          <TabsContent value="change" className="mt-4">
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4">Available Plans</h2>
              {availablePlans.length === 0 ? (
                <p className="text-muted-foreground">No other plans available</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {availablePlans.map(plan => (
                    <PlanChangeCard
                      key={plan.id}
                      plan={plan}
                      isCurrent={subscription?.plan_id === plan.id}
                      onSelect={() => handleChangePlan(plan.id)}
                    />
                  ))}
                </div>
              )}
              {changing && (
                <div className="flex items-center justify-center gap-2 mt-4 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" /> Updating plan...
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
